import { Injectable } from "@angular/core";
import moment from "moment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { AchAccountInfo } from "../../definitions/ach-account-info";
import { FileRecord } from "../../definitions/api/file-record";
import { HttpOptionsBuilder } from "../../definitions/api/http-options-builder";
import { VendorReviewSummary } from "../../definitions/api/open/vendor-review-summary";
import { ActionItem } from "../../definitions/api/shared/action-item";
import { BankAccountTemplate } from "../../definitions/api/shared/bank-account-template";
import { BeneficiaryCreate } from "../../definitions/api/shared/beneficiary-create";
import { DailyStats } from "../../definitions/api/shared/daily-stats";
import { Stats } from "../../definitions/api/shared/stats";
import { VendorAccount } from "../../definitions/vendor-account";
import { VendorAccountRequest } from "../../definitions/vendor-account-request";
import { AppConfigService } from "../app.config.service";
import { ApiService } from "./api.service";
import { BaseApiService } from "./base-api.service";

@Injectable({ providedIn: "root" })
export class VendorService extends BaseApiService {
	constructor(
		private api: ApiService,
		appConfigService: AppConfigService
	) {
		super(`mpapi/vendor`, appConfigService.getConfigValue("API_ENDPOINT"));
	}

	account(): Observable<VendorAccount> {
		return this.api.get(this.url());
	}

	accountCreate(request: VendorAccountRequest): Observable<void> {
		return this.api.post(this.url(), request);
	}

	updateProductFiles(files: File[], remainingFileRecords: FileRecord[]): Observable<void> {
		const data = new FormData();
		data.append("fileRecords", JSON.stringify(remainingFileRecords));
		files?.forEach((file) => {
			data.append("files", file);
		});
		return this.api.put(this.url("/product-files"), data);
	}

	createAchCustomer(request: AchAccountInfo): Observable<void> {
		return this.api.postAndThrowError(this.url("/ach-customer"), request);
	}

	checkVendorAgreementSigningStatus(): Observable<VendorAccount> {
		return this.api.put(this.url("/checkVendorAgreementSigningStatus"), {});
	}

	bankTemplate(country?: string, currency?: string): Observable<BankAccountTemplate> {
		const ob = HttpOptionsBuilder.builder();
		if (country) {
			ob.param("country", country);
		}
		if (currency) {
			ob.param("currency", currency);
		}
		return this.api.get(this.url("/ofx-bank-template"), undefined, ob.build());
	}

	beneficiaryCreate(create: BeneficiaryCreate): Observable<void> {
		return this.api.post(this.url("/ofx-beneficiary"), create);
	}

	count(): Observable<number> {
		return this.api.get<number>(this.url("/count"), "errors.get-vendor-count-failed");
	}

	stats(): Observable<Stats> {
		return this.api.get(this.url("/stats"), "errors.retrieve-financial-statistics-failed");
	}

	dailyStats(startDate?: string, endDate?: string): Observable<DailyStats[]> {
		const options = HttpOptionsBuilder.builder()
			.param("startDate", startDate)
			.param("endDate", endDate)
			.build();
		return this.api.get(
			this.url("/stats/daily"),
			"errors.retrieve-daily-financial-statistics-failed",
			options
		);
	}

	resendInviteEmail(userIds: { userIds: Array<string> }) {
		return this.api.put(this.url(`/user/invite`), userIds, "errors.resend-email-to-users-failed");
	}

	actionItems(): Observable<Array<ActionItem>> {
		return this.api.get(this.url("/action-items"), "errors.get-action-items-failed");
	}

	vendorRating(vendorId: string): Observable<string> {
		return this.api
			.get<{
				vendorRating: string;
			}>(this.url(`/${vendorId}/rating`), "errors.get-vendor-rating-failed")
			.pipe(map((rating) => rating.vendorRating));
	}

	vendorReviews(
		vendorId: string,
		beforeTime?: Date,
		maxReviews?: number
	): Observable<VendorReviewSummary[]> {
		const ob = HttpOptionsBuilder.builder();
		if (beforeTime) {
			ob.param("beforeTime", moment.utc(beforeTime).toISOString());
		}
		if (maxReviews) {
			ob.param("maxReviews", `${maxReviews}`);
		}
		return this.api.get(
			this.url(`/${vendorId}/reviews`),
			"errors.get-vendor-reviews-failed",
			ob.build()
		);
	}
}
